import { IconButton, OdinIcon, OdinIconSize } from '@myosh/odin-components';
import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../helpers/util';
import { useAppDispatch } from '../../redux/hooks';
import { JobStatus, removeJob, type SubmittedJobDetails } from '../../redux/slices/running-jobs';

interface RunningJobsPanelProps {
  submittedJobs: SubmittedJobDetails[];
}

const RunningJobsPanel: React.FC<RunningJobsPanelProps> = ({ submittedJobs }) => {
  const panelButtonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useAppDispatch();

  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [hasViewedJobs, setHasViewedJobs] = useState(false);

  const onPanelButtonClick = () => setIsPanelVisible(!isPanelVisible);
  const onCloseButtonClick = () => setIsPanelVisible(false);

  const getPanelButtonStatusIcon = () => {
    const statusIconClasses = 'bt-absolute bt-top-[-10px] bt-right-[-10px]';
    const latestJobStatus = submittedJobs[0].status;

    if (submittedJobs.some((job: SubmittedJobDetails) => job.status === JobStatus.InProgress)) {
      return <OdinIcon icon="Loader" size={OdinIconSize.ExtraSmall} className={statusIconClasses} spin />;
    } else if (hasViewedJobs) {
      return null;
    } else if (latestJobStatus === JobStatus.Failed) {
      return (
        <OdinIcon icon="CircleAlert" size={OdinIconSize.ExtraSmall} className={`${statusIconClasses} bt-text-error`} />
      );
    } else if (latestJobStatus === JobStatus.Success) {
      return (
        <OdinIcon
          icon="CircleCheck"
          size={OdinIconSize.ExtraSmall}
          className={`${statusIconClasses} bt-text-success`}
        />
      );
    } else {
      return null;
    }
  };

  const onJobClick = (job: SubmittedJobDetails) => {
    if (job && job.url && job.name && job.status === JobStatus.Success) {
      window.open(job.url);
    }
  };

  const onJobRemove = (id: number) => {
    if (id) {
      dispatch(removeJob(id));
    }
  };

  const getStatusIcon = (status?: JobStatus) => {
    if (status) {
      if (status === JobStatus.InProgress) {
        return <OdinIcon icon="Loader" size={OdinIconSize.ExtraSmall} spin />;
      } else if (status === JobStatus.Success) {
        return <OdinIcon icon="CircleCheck" size={OdinIconSize.ExtraSmall} className="bt-text-success" />;
      } else if (status === JobStatus.Failed) {
        return <OdinIcon icon="CircleAlert" size={OdinIconSize.ExtraSmall} className="bt-text-error" />;
      }
    }
  };

  useEffect(() => {
    if (isPanelVisible) {
      if (submittedJobs.every((job: SubmittedJobDetails) => job.status !== JobStatus.InProgress)) {
        setHasViewedJobs(true);
      } else {
        setHasViewedJobs(false);
      }
    }

    if (submittedJobs.length === 0) {
      setIsPanelVisible(false);
    }
  }, [isPanelVisible, submittedJobs]);

  return (
    <div className="bt-flex bt-items-center">
      {submittedJobs && submittedJobs.length > 0 && (
        <div
          data-cy="running-jobs-container"
          className="bt-relative bt-mt-[0.4rem] bt-space-y-2 bt-rounded-b-lg bt-bg-mono-1 bt-p-4"
        >
          <button
            data-cy="running-jobs-toggle-button"
            ref={panelButtonRef}
            onClick={onPanelButtonClick}
            className="bt-relative"
          >
            <OdinIcon icon="FolderDown" />
            {getPanelButtonStatusIcon()}
          </button>
          {isPanelVisible && (
            <div
              data-cy="running-jobs-panel"
              className="bt-z-4001 bt-absolute bt-top-10 bt-min-w-[14rem] bt-max-w-[25rem] bt-rounded-lg bt-bg-gray-5 bt-shadow-lg"
            >
              <div data-cy="running-jobs-header" className="bt-flex bt-items-center bt-justify-between bt-p-4">
                <span data-cy="running-jobs-header-title" className="bt-whitespace-nowrap">
                  Job Status
                </span>
                <IconButton
                  data-cy="running-jobs-close-button"
                  aria-label="Close running jobs panel"
                  onClick={onCloseButtonClick}
                  icon="X"
                  className="bt-rounded bt-p-1"
                />
              </div>
              <div data-cy="running-jobs-list" className="bt-space-y-2 bt-rounded-b-lg bt-bg-mono-1 bt-p-4">
                {submittedJobs.map((job: SubmittedJobDetails) => {
                  const itemStyles = cn('bt-flex bt-gap-2 bt-items-center bt-min-w-0 bt-w-full', {
                    'hover:bt-cursor-pointer': job.status === JobStatus.Success,
                  });

                  const removeIconStyles = cn(
                    'bt-w-4 bt-h-4 bt-rounded-full bt-hover:bg-primary-5 hover:bt-cursor-pointer',
                    {
                      'bt-pointer-events-none': job.status === JobStatus.InProgress,
                    }
                  );

                  return (
                    <div
                      key={job.id}
                      data-cy={`running-job-${job.id}`}
                      className="bt-flex bt-items-center bt-p-1 [&:has(div:hover)]:bt-bg-primary-5"
                    >
                      <div
                        data-cy={`running-job-item-${job.id}`}
                        className={itemStyles}
                        onClick={() => onJobClick(job)}
                      >
                        <OdinIcon icon="FileText" />
                        <span className="bt-flex-1 bt-truncate" title={job.name}>
                          {job.name}
                        </span>
                        <span>{getStatusIcon(job.status)}</span>
                      </div>
                      <span
                        data-cy={`running-job-remove-${job.id}`}
                        className={removeIconStyles}
                        onClick={() => onJobRemove(job.id)}
                      >
                        {job.status !== JobStatus.InProgress && <OdinIcon icon="X" size={OdinIconSize.ExtraSmall} />}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RunningJobsPanel;
